import {
  analyticsApi,
  integrationApi,
  type AnalyticsRangeSelection,
  type SearchConsoleBreakdownItem,
  type SearchConsoleFinding,
  type SearchConsoleImpactFinding,
  type SearchConsoleAnalyticsResponse,
  type SearchConsolePageImpact,
  type SearchConsoleTimeseriesPoint,
} from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  Minus,
  Search,
} from 'lucide-react';
import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { calculatePercentChange, cn, formatCompactNumber } from '@/lib/utils';

type SearchMetric = 'clicks' | 'impressions' | 'ctr' | 'position';

const METRICS: Array<{
  id: SearchMetric;
  label: string;
  help: string;
}> = [
  {
    id: 'clicks',
    label: 'Clicks',
    help: 'Visits from Google Search to this property.',
  },
  {
    id: 'impressions',
    label: 'Impressions',
    help: 'Times a page from this property appeared in Google Search.',
  },
  {
    id: 'ctr',
    label: 'CTR',
    help: 'Clicks divided by impressions for the selected period.',
  },
  {
    id: 'position',
    label: 'Avg. position',
    help: 'Impression-weighted average result position. Lower is better.',
  },
];

const formatPercent = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);

const formatMetric = (metric: SearchMetric, value: number): string => {
  if (metric === 'ctr') {
    return formatPercent(value);
  }
  if (metric === 'position') {
    return value > 0 ? value.toFixed(1) : '—';
  }
  return formatCompactNumber(value);
};

const MetricDelta = ({
  current,
  previous,
  lowerIsBetter = false,
}: {
  current: number;
  previous: number;
  lowerIsBetter?: boolean;
}) => {
  const change = calculatePercentChange(current, previous);
  if (change === null) {
    return <span className="text-xs font-medium text-positive">New</span>;
  }
  if (Math.abs(change) < 0.5) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-ink-subtle">
        <Minus className="size-3" /> 0%
      </span>
    );
  }
  const rising = change > 0;
  const favorable = lowerIsBetter ? !rising : rising;
  const Icon = rising ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        favorable ? 'text-positive' : 'text-critical',
      )}
    >
      <Icon className="size-3.5" />
      {Math.abs(change).toFixed(0)}%
    </span>
  );
};

const formatChartDate = (value: string): string => {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : value;
};

const SearchTrend = ({
  points,
  metric,
}: {
  points: SearchConsoleTimeseriesPoint[];
  metric: SearchMetric;
}) => {
  const width = 900;
  const height = 220;
  const paddingX = 18;
  const paddingY = 22;
  const normalized =
    points.length > 1
      ? points
      : points.length === 1
        ? [points[0], points[0]]
        : [];
  if (!normalized.length) {
    return (
      <div className="flex h-[220px] items-center justify-center text-sm text-ink-muted">
        No published data in this period
      </div>
    );
  }
  const values = normalized.map((point) => point[metric]);
  const minimum = metric === 'position' ? Math.min(...values) : 0;
  const maximum = Math.max(...values, minimum + 1);
  const span = Math.max(maximum - minimum, 1);
  const chartPoints = normalized.map((point, index) => ({
    x: paddingX + (index / (normalized.length - 1)) * (width - paddingX * 2),
    y: paddingY + ((maximum - point[metric]) / span) * (height - paddingY * 2),
  }));
  const line = chartPoints
    .map((point, index) => `${index ? 'L' : 'M'} ${point.x} ${point.y}`)
    .join(' ');
  const area = `${line} L ${chartPoints.at(-1)?.x ?? width - paddingX} ${height - paddingY} L ${paddingX} ${height - paddingY} Z`;
  const labelIndexes = Array.from(
    new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]),
  ).filter((index) => index >= 0 && points[index]);

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-[210px] w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${metric} from Google Search Console`}
      >
        {[0, 0.5, 1].map((fraction) => {
          const y = paddingY + fraction * (height - paddingY * 2);
          const value = maximum - span * fraction;
          return (
            <g key={fraction}>
              <line
                x1={paddingX}
                x2={width - paddingX}
                y1={y}
                y2={y}
                className="stroke-border"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={paddingX + 4}
                y={Math.max(y - 6, 12)}
                className="fill-ink-subtle text-[10px]"
              >
                {formatMetric(metric, value)}
              </text>
            </g>
          );
        })}
        <path d={area} className="fill-accent/8" />
        <path
          d={line}
          fill="none"
          className="stroke-accent"
          strokeWidth="2.25"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {chartPoints.map((point, index) => (
          <circle
            key={`${point.x}-${point.y}-${index}`}
            cx={point.x}
            cy={point.y}
            r="8"
            className="fill-transparent"
          >
            <title>{`${formatChartDate(normalized[index].timestamp)}: ${formatMetric(metric, normalized[index][metric])}`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between px-4 text-xs text-ink-subtle">
        {labelIndexes.map((index) => (
          <span key={`${points[index].timestamp}-${index}`}>
            {formatChartDate(points[index].timestamp)}
          </span>
        ))}
      </div>
    </div>
  );
};

const displayPage = (value: string): string => {
  try {
    const url = new URL(value);
    return `${url.pathname}${url.search}` || '/';
  } catch {
    return value;
  }
};

const BreakdownTable = ({
  items,
  dimension,
}: {
  items: SearchConsoleBreakdownItem[];
  dimension: 'query' | 'page';
}) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[620px] text-sm">
      <thead className="text-xs text-ink-subtle">
        <tr className="border-b border-border">
          <th className="px-1 py-2 text-left font-medium">
            {dimension === 'query' ? 'Query' : 'Page'}
          </th>
          <th className="px-2 py-2 text-right font-medium">Clicks</th>
          <th className="px-2 py-2 text-right font-medium">Impressions</th>
          <th className="px-2 py-2 text-right font-medium">CTR</th>
          <th className="px-1 py-2 text-right font-medium">Position</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.name} className="border-b border-border last:border-0">
            <td className="max-w-[30rem] truncate px-1 py-2.5 font-medium text-ink">
              {dimension === 'page' ? displayPage(item.name) : item.name}
            </td>
            <td className="px-2 py-2.5 text-right tabular-nums text-ink">
              {formatCompactNumber(item.clicks)}
            </td>
            <td className="px-2 py-2.5 text-right tabular-nums text-ink-muted">
              {formatCompactNumber(item.impressions)}
            </td>
            <td className="px-2 py-2.5 text-right tabular-nums text-ink-muted">
              {formatPercent(item.ctr)}
            </td>
            <td className="px-1 py-2.5 text-right tabular-nums text-ink-muted">
              {item.position.toFixed(1)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const formatUsdMinor = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value % 100 === 0 ? 0 : 2,
  }).format(value / 100);

const ImpactTable = ({
  items,
  signupAttribution,
  paidAttribution,
}: {
  items: SearchConsolePageImpact[];
  signupAttribution: 'available' | 'unavailable';
  paidAttribution: 'available' | 'identity_required' | 'not_connected';
}) => {
  if (!items.length) {
    return (
      <div className="py-8 text-center text-sm text-ink-muted">
        No search landing pages in this period
      </div>
    );
  }

  const signupsAvailable = signupAttribution === 'available';
  const paidAvailable = paidAttribution === 'available';
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-sm">
        <thead className="text-xs text-ink-subtle">
          <tr className="border-b border-border">
            <th className="px-1 py-2 text-left font-medium">Landing page</th>
            <th className="px-2 py-2 text-right font-medium">Clicks</th>
            <th className="px-2 py-2 text-right font-medium">Visitors</th>
            <th className="px-2 py-2 text-right font-medium">
              <span className="inline-flex items-center justify-end gap-1">
                Signups
                <HelpTooltip label="About attributed signups" className="size-5">
                  Recorded signup_completed events are attributed to the entry
                  page of the same browser session.
                </HelpTooltip>
              </span>
            </th>
            <th className="px-2 py-2 text-right font-medium">
              <span className="inline-flex items-center justify-end gap-1">
                Paid
                <HelpTooltip label="About attributed paid users" className="size-5">
                  Paid users are shown only when a signup user ID exactly
                  matches a RevenueCat customer or alias. Unmatched purchases
                  are never guessed.
                </HelpTooltip>
              </span>
            </th>
            <th className="px-1 py-2 text-right font-medium">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.page} className="border-b border-border last:border-0">
              <td className="max-w-[24rem] px-1 py-2.5">
                <p className="truncate font-medium text-ink">
                  {displayPage(item.page)}
                </p>
                <p className="mt-0.5 truncate text-xs text-ink-subtle">
                  {formatCompactNumber(item.impressions)} impressions · #
                  {item.position.toFixed(1)}
                </p>
              </td>
              <td className="px-2 py-2.5 text-right tabular-nums text-ink">
                {formatCompactNumber(item.clicks)}
              </td>
              <td className="px-2 py-2.5 text-right tabular-nums text-ink">
                {formatCompactNumber(item.visitors)}
              </td>
              <td className="px-2 py-2.5 text-right tabular-nums">
                {signupsAvailable ? (
                  <>
                    <span className="text-ink">
                      {formatCompactNumber(item.signups)}
                    </span>
                    {item.signupRate !== null ? (
                      <span className="ml-1.5 text-xs text-ink-subtle">
                        {formatPercent(item.signupRate)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="text-ink-subtle">—</span>
                )}
              </td>
              <td className="px-2 py-2.5 text-right tabular-nums text-ink">
                {paidAvailable ? formatCompactNumber(item.paidUsers) : '—'}
              </td>
              <td className="px-1 py-2.5 text-right tabular-nums text-ink">
                {paidAvailable ? formatUsdMinor(item.revenueUsdMinor) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const impactFindingCopy = (finding: SearchConsoleImpactFinding) => {
  if (finding.kind === 'traffic_without_signups') {
    return {
      title: `${displayPage(finding.page)} gets traffic but no signups`,
      detail: `${formatCompactNumber(finding.visitors)} visitors from ${formatCompactNumber(finding.clicks)} search clicks`,
      help: 'This landing page had at least 10 visitors in the selected period and no recorded signup_completed events.',
    };
  }
  if (finding.kind === 'high_conversion_low_visibility') {
    return {
      title: `${displayPage(finding.page)} converts; visibility can grow`,
      detail: `${formatPercent(finding.signupRate ?? 0)} signup rate · position ${finding.position.toFixed(1)}`,
      help: 'This page recorded at least two signups and a 3% signup rate, but its average search position remains below the top seven.',
    };
  }
  return {
    title: `Search grew without signup growth on ${displayPage(finding.page)}`,
    detail: `${formatCompactNumber(finding.clicks)} clicks · ${formatCompactNumber(finding.signups)} signups`,
    help: 'Search clicks increased by at least 30% while recorded signups did not improve against the preceding period.',
  };
};

const ImpactFindings = ({
  findings,
}: {
  findings: SearchConsoleImpactFinding[];
}) => {
  if (!findings.length) {
    return null;
  }
  return (
    <ol className="divide-y divide-border">
      {findings.map((finding) => {
        const copy = impactFindingCopy(finding);
        return (
          <li
            key={`${finding.kind}-${finding.page}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">
                {copy.title}
              </p>
              <p className="mt-0.5 truncate text-xs text-ink-subtle">
                {copy.detail}
              </p>
            </div>
            <HelpTooltip label={`Why this finding matters: ${displayPage(finding.page)}`}>
              {copy.help}
            </HelpTooltip>
          </li>
        );
      })}
    </ol>
  );
};

const findingCopy = (finding: SearchConsoleFinding) => {
  if (finding.kind === 'declining') {
    return {
      title: `Clicks fell for “${finding.query}”`,
      help: 'Clicks are at least 30% lower than the preceding period, with at least 10 previous clicks.',
    };
  }
  if (finding.kind === 'near_page_one') {
    return {
      title: `“${finding.query}” is close to page one`,
      help: 'This query has meaningful impressions and an average position between 8 and 15.',
    };
  }
  return {
    title: `Low CTR for “${finding.query}”`,
    help: 'This query ranks in the top 20 and has at least 100 impressions, but fewer than 2% become clicks.',
  };
};

const Findings = ({ findings }: { findings: SearchConsoleFinding[] }) => {
  if (!findings.length) {
    return (
      <div className="py-8 text-center text-sm text-ink-muted">
        No notable search changes
      </div>
    );
  }
  return (
    <ol className="divide-y divide-border">
      {findings.map((finding, index) => {
        const copy = findingCopy(finding);
        return (
          <li
            key={`${finding.kind}-${finding.query}-${finding.page}`}
            className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-3 py-3"
          >
            <span className="flex size-7 items-center justify-center rounded-md bg-surface-muted text-xs font-semibold text-ink-muted">
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">
                {copy.title}
              </p>
              <p className="mt-0.5 truncate text-xs text-ink-subtle">
                {displayPage(finding.page)} ·{' '}
                {formatCompactNumber(finding.impressions)} impressions ·{' '}
                {formatPercent(finding.ctr)} CTR · #
                {finding.position.toFixed(1)}
              </p>
            </div>
            <HelpTooltip label={`Why this finding matters: ${finding.query}`}>
              {copy.help}
            </HelpTooltip>
          </li>
        );
      })}
    </ol>
  );
};

export const SearchPerformance = ({
  projectId,
  range,
  previewData,
}: {
  projectId: string;
  range: AnalyticsRangeSelection;
  previewData?: SearchConsoleAnalyticsResponse;
}) => {
  const [metric, setMetric] = React.useState<SearchMetric>('clicks');
  const [expanded, setExpanded] = React.useState(false);
  const integrationsQuery = useQuery({
    queryKey: ['integrations', projectId],
    queryFn: () => integrationApi.listIntegrations(projectId),
    enabled: !previewData,
  });
  const connected = previewData
    ? true
    : integrationsQuery.data?.integrations.some(
        (integration) =>
          integration.provider === 'google_search_console' &&
          integration.status !== 'disconnected',
      );
  const searchQuery = useQuery({
    queryKey: ['analytics', 'project', projectId, 'search-console', range],
    queryFn: () => analyticsApi.getSearchConsole(projectId, range),
    enabled: Boolean(connected && !previewData),
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
  });

  if (!previewData && !integrationsQuery.isLoading && !connected) {
    return null;
  }
  if (!previewData && (integrationsQuery.isLoading || searchQuery.isLoading)) {
    return <Skeleton className="h-[280px] rounded-card" />;
  }
  if (!previewData && searchQuery.isError) {
    return (
      <Card className="shadow-none">
        <CardHeader className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Search className="size-4 text-ink-muted" aria-hidden="true" />
            <CardTitle>Search performance</CardTitle>
          </div>
          <Badge tone="critical">Unavailable</Badge>
        </CardHeader>
      </Card>
    );
  }
  const data = previewData ?? searchQuery.data;
  if (!data || data.status === 'not_connected') {
    return null;
  }
  const impact = data.impact ?? {
    pages: [],
    findings: [],
    signupAttribution: 'unavailable' as const,
    paidAttribution: 'not_connected' as const,
  };
  const metrics = [
    {
      id: 'clicks' as const,
      value: data.summary.clicks,
      previous: data.summary.previousClicks,
    },
    {
      id: 'impressions' as const,
      value: data.summary.impressions,
      previous: data.summary.previousImpressions,
    },
    {
      id: 'ctr' as const,
      value: data.summary.ctr,
      previous: data.summary.previousCtr,
    },
    {
      id: 'position' as const,
      value: data.summary.position,
      previous: data.summary.previousPosition,
    },
  ];
  const latestDate = data.latestDate
    ? new Date(`${data.latestDate}T00:00:00Z`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;
  const defaultTab = impact.pages.length ? 'impact' : 'queries';
  const featuredImpact = impact.findings.slice(0, 3);
  const featuredSearch = data.findings.slice(0, 3);

  return (
    <Card className="overflow-hidden shadow-none">
      <CardHeader className="flex items-center justify-between gap-3 border-b border-border">
        <div className="flex items-center gap-1">
          <Search className="size-4 text-ink-muted" aria-hidden="true" />
          <CardTitle>Search performance</CardTitle>
          <HelpTooltip label="About Search performance">
            Google Search Console reports aggregate search activity and usually
            arrives two to three days late. GainSignal never fills unpublished
            dates with zeroes.
          </HelpTooltip>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-subtle">
          {data.status === 'syncing' ? (
            <Badge tone="accent">Syncing</Badge>
          ) : data.status === 'needs_attention' ? (
            <Badge tone="critical">Needs attention</Badge>
          ) : null}
          {latestDate ? <span>Through {latestDate}</span> : null}
        </div>
      </CardHeader>

      <div className="grid grid-cols-2 border-b border-border lg:grid-cols-4">
        {metrics.map((item, index) => {
          const definition = METRICS.find((entry) => entry.id === item.id)!;
          return (
            <div
              key={item.id}
              className={cn(
                'min-w-0 border-border px-5 py-4',
                index % 2 === 0 ? 'border-r' : '',
                index < 2 ? 'border-b lg:border-b-0' : '',
                index === 1 ? 'lg:border-r' : '',
                index === 2 ? 'lg:border-r' : '',
                expanded && metric === item.id && 'bg-surface-muted',
              )}
            >
              <span className="flex items-center gap-1 text-xs text-ink-muted">
                {definition.label}
                <HelpTooltip
                  label={`About ${definition.label}`}
                  className="size-5"
                >
                  {definition.help}
                </HelpTooltip>
              </span>
              <button
                type="button"
                aria-pressed={expanded && metric === item.id}
                onClick={() => {
                  setMetric(item.id);
                  setExpanded(true);
                }}
                className="mt-1 flex w-full items-baseline gap-2 rounded text-left focus-visible:outline-accent"
              >
                <strong className="text-xl font-semibold tracking-tight text-ink">
                  {formatMetric(item.id, item.value)}
                </strong>
                <MetricDelta
                  current={item.value}
                  previous={item.previous}
                  lowerIsBetter={item.id === 'position'}
                />
              </button>
            </div>
          );
        })}
      </div>

      {(featuredImpact.length > 0 || featuredSearch.length > 0) && (
        <div className="px-5 sm:px-6">
          {featuredImpact.length ? (
            <ImpactFindings findings={featuredImpact} />
          ) : (
            <Findings findings={featuredSearch} />
          )}
        </div>
      )}

      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-between border-t border-border px-5 py-3 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink sm:px-6"
      >
        {expanded ? 'Hide search details' : 'Explore search'}
        <ChevronDown
          className={cn(
            'size-4 transition-transform',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className="border-t border-border">
          <div className="border-b border-border px-4 py-4 sm:px-5">
            <SearchTrend points={data.timeseries} metric={metric} />
          </div>

          <Tabs defaultValue={defaultTab} className="px-4 pb-4 sm:px-5">
            <TabsList>
              <TabsTrigger value="impact">Impact</TabsTrigger>
              <TabsTrigger value="opportunities">
                Opportunities
                {impact.findings.length || data.findings.length
                  ? ` ${impact.findings.length || data.findings.length}`
                  : ''}
              </TabsTrigger>
              <TabsTrigger value="queries">Queries</TabsTrigger>
            </TabsList>
            <TabsContent value="impact" className="mt-0">
              <ImpactTable
                items={impact.pages}
                signupAttribution={impact.signupAttribution}
                paidAttribution={impact.paidAttribution}
              />
            </TabsContent>
            <TabsContent value="opportunities" className="mt-0">
              {impact.findings.length ? (
                <ImpactFindings findings={impact.findings} />
              ) : (
                <Findings findings={data.findings} />
              )}
            </TabsContent>
            <TabsContent value="queries" className="mt-0">
              <BreakdownTable
                items={data.breakdowns.query}
                dimension="query"
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Card>
  );
};
